import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, errMsg } from '../lib/api';
import { formatCnpjInput, formatPhoneInput, TIPO_LABEL } from '../lib/format';
import type { Member, Restaurant } from '../lib/types';
import { Button, Field, Sheet } from './ui';
import { useToast } from './feedback';

const EMPTY = {
  nome: '', razaoSocial: '', cnpj: '', tipo: 'escola', contatoNome: '', contatoTelefone: '', contatoEmail: '', nutricionistaResponsavelId: '',
  // Migração: nota do sistema anterior (texto para aceitar "8,5"), escala e peso.
  notaInicial: '', escala: '100' as '100' | '10', notaInicialPeso: '1',
};

/** "8,5" na escala 0–10 → 85. Vazio → null. Inválido → undefined. */
function notaParaCem(v: string, escala: '100' | '10'): number | null | undefined {
  if (!v.trim()) return null;
  const n = Number(v.replace(',', '.'));
  if (!Number.isFinite(n) || n < 0 || n > (escala === '10' ? 10 : 100)) return undefined;
  return Math.round(escala === '10' ? n * 10 : n);
}

export function RestaurantForm({ tid, open, onClose, initial, onSaved }: {
  tid: string; open: boolean; onClose: () => void; initial?: Restaurant | null; onSaved?: (id: string) => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [f, setF] = useState(EMPTY);
  const [error, setError] = useState('');
  const members = useQuery({ queryKey: ['members', tid], queryFn: () => api.get<{ members: Member[] }>(`/api/t/${tid}/members`), enabled: open });

  useEffect(() => {
    if (!open) return;
    setError('');
    setF(
      initial
        ? {
            nome: initial.nome, razaoSocial: initial.razaoSocial ?? '', cnpj: initial.cnpj ?? '', tipo: initial.tipo,
            contatoNome: initial.contatoNome ?? '', contatoTelefone: initial.contatoTelefone ?? '', contatoEmail: initial.contatoEmail ?? '',
            nutricionistaResponsavelId: initial.nutricionistaResponsavelId ?? '',
            notaInicial: initial.notaInicial !== null && initial.notaInicial !== undefined ? String(initial.notaInicial) : '', escala: '100',
            notaInicialPeso: String(initial.notaInicialPeso ?? 1),
          }
        : { ...EMPTY, nutricionistaResponsavelId: members.data?.members.find((m) => m.isMe)?.userId ?? '' },
    );
  }, [open, initial]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Padrão: quem cria é a responsável.
    if (open && !initial && !f.nutricionistaResponsavelId && members.data) {
      const mine = members.data.members.find((m) => m.isMe && m.role !== 'visualizador');
      if (mine) setF((x) => ({ ...x, nutricionistaResponsavelId: mine.userId }));
    }
  }, [members.data, open]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: () => {
      const { notaInicial: _n, escala: _e, notaInicialPeso: _p, ...rest } = f;
      const payload = {
        ...rest,
        nutricionistaResponsavelId: f.nutricionistaResponsavelId || null,
        notaInicial: notaParaCem(f.notaInicial, f.escala) ?? null,
        notaInicialPeso: Math.max(1, Math.min(100, Math.round(Number(f.notaInicialPeso) || 1))),
      };
      return initial ? api.patch<{ ok: true }>(`/api/t/${tid}/restaurants/${initial.id}`, payload).then(() => ({ id: initial.id })) : api.post<{ id: string }>(`/api/t/${tid}/restaurants`, payload);
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['restaurants', tid] });
      qc.invalidateQueries({ queryKey: ['restaurant', tid] });
      qc.invalidateQueries({ queryKey: ['home', tid] });
      toast(initial ? 'Restaurante atualizado' : 'Restaurante criado');
      onClose();
      onSaved?.(r.id);
    },
    onError: (e) => setError(errMsg(e)),
  });

  const set = (k: keyof typeof EMPTY) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setF((x) => ({ ...x, [k]: e.target.value }));
  const nutris = (members.data?.members ?? []).filter((m) => m.role !== 'visualizador');

  return (
    <Sheet open={open} onClose={onClose} title={initial ? 'Editar restaurante' : 'Novo restaurante'}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (f.nome.trim().length < 2) return setError('Informe o nome do restaurante.');
          if (notaParaCem(f.notaInicial, f.escala) === undefined) return setError(`A nota do sistema anterior deve ser um número de 0 a ${f.escala === '10' ? '10' : '100'}.`);
          save.mutate();
        }}
      >
        <Field label="Nome da unidade"><input className="input" placeholder="Refeitório Central Alfa" value={f.nome} onChange={set('nome')} /></Field>
        <Field label="Razão social"><input className="input" placeholder="Refeitório Central Ltda" value={f.razaoSocial} onChange={set('razaoSocial')} /></Field>
        <Field label="CNPJ"><input className="input mono" inputMode="numeric" placeholder="00.000.000/0000-00" value={f.cnpj} onChange={(e) => setF((x) => ({ ...x, cnpj: formatCnpjInput(e.target.value) }))} /></Field>
        <div className="field">
          <span className="label">Tipo de UAN</span>
          <div className="row wrap gap-6">
            {Object.entries(TIPO_LABEL).map(([k, l]) => (
              <button type="button" key={k} className={`chip ${f.tipo === k ? 'on' : ''}`} style={{ height: 34, fontSize: 12.5 }} onClick={() => setF((x) => ({ ...x, tipo: k }))} aria-pressed={f.tipo === k}>{l}</button>
            ))}
          </div>
        </div>
        <Field label="Contato responsável"><input className="input" placeholder="Nome" value={f.contatoNome} onChange={set('contatoNome')} /></Field>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))', gap: '0 10px' }}>
          <Field label="Telefone"><input className="input mono" inputMode="tel" placeholder="(11) 98812-4470" value={f.contatoTelefone} onChange={(e) => setF((x) => ({ ...x, contatoTelefone: formatPhoneInput(e.target.value) }))} /></Field>
          <Field label="E-mail do contato"><input className="input" type="email" inputMode="email" placeholder="contato@empresa.com.br" value={f.contatoEmail} onChange={set('contatoEmail')} /></Field>
        </div>
        <Field label="Nutricionista responsável">
          <select className="select" value={f.nutricionistaResponsavelId} onChange={set('nutricionistaResponsavelId')}>
            <option value="">Sem responsável</option>
            {nutris.map((m) => <option key={m.userId} value={m.userId}>{m.nome}</option>)}
          </select>
        </Field>

        {/* Migração de outro sistema: a nota atual entra na média e as visitas novas seguem a partir dela. */}
        <div style={{ padding: 12, borderRadius: 12, background: 'var(--soft)', marginBottom: 14 }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 2 }}>Nota do sistema anterior <span className="small" style={{ fontWeight: 400 }}>(opcional)</span></div>
          <div className="small" style={{ marginBottom: 10, lineHeight: 1.45 }}>
            Se a unidade já tinha nota em outro sistema, informe aqui. Ela entra na média e as próximas visitas continuam a partir dela.
          </div>
          <div className="row wrap gap-8" style={{ alignItems: 'flex-end' }}>
            <label className="stack gap-6" style={{ flex: '1 1 110px', cursor: 'default' }}>
              <span className="label">Nota atual</span>
              <input className="input sm mono" inputMode="decimal" placeholder={f.escala === '10' ? 'ex.: 8,5' : 'ex.: 85'} value={f.notaInicial} onChange={set('notaInicial')} />
            </label>
            <div className="stack gap-6" style={{ flex: 'none' }}>
              <span className="label">Escala</span>
              <div className="seg">
                <button type="button" className={f.escala === '100' ? 'on' : ''} onClick={() => setF((x) => ({ ...x, escala: '100' }))}>0 a 100</button>
                <button type="button" className={f.escala === '10' ? 'on' : ''} onClick={() => setF((x) => ({ ...x, escala: '10' }))}>0 a 10</button>
              </div>
            </div>
          </div>
          {f.notaInicial.trim() !== '' && (
            <label className="stack gap-6" style={{ marginTop: 10, cursor: 'default' }}>
              <span className="label">Essa nota resume quantas visitas?</span>
              <input className="input sm mono" inputMode="numeric" style={{ maxWidth: 120 }} value={f.notaInicialPeso} onChange={set('notaInicialPeso')} />
              <span className="small" style={{ lineHeight: 1.45 }}>
                Quanto maior, mais devagar a média muda com as visitas novas. Na dúvida, deixe 1.
                {notaParaCem(f.notaInicial, f.escala) != null && f.escala === '10' && <> Fica <strong>{notaParaCem(f.notaInicial, f.escala)}</strong> na escala de 0 a 100.</>}
              </span>
            </label>
          )}
        </div>
        {error &&<div className="error-box" role="alert" style={{ marginBottom: 12 }}>{error}</div>}
        <Button variant="dark" size="lg" block type="submit" loading={save.isPending}>{initial ? 'Salvar alterações' : 'Salvar restaurante'}</Button>
      </form>
    </Sheet>
  );
}
